import React, { useState, useEffect, useMemo } from 'react';
import {  CircularProgress } from '@mui/material';
import FormDialog from './dialog';
import { MdEditNote } from "react-icons/md";
import { Tooltip } from 'react-tooltip';
import DashboardNavbar from "../dashboardNavbar"
import DataTable from '../ui/DataTable';
import GroupedRowActionSheet, { ICON_PROPS } from '../ui/GroupedRowActionSheet';
import { Pencil } from 'lucide-react';
import { useConfirm } from '../ui/ConfirmProvider';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';
import {
  SETTING_LIST_DEFAULTS,
  SETTING_SORT_FIELDS,
  getNextSettingSort,
  sortSettingsLocally,
} from './settingListApi';
import { LabelWithHelp } from '../ui/FieldHelp';
import { SYSTEM_SETTINGS_FIELD_HELP as HELP } from '../ui/settingsFieldHelp';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const initialValue = { role: "", percentage: "" };

const Grids = ({ selectedTitle }) => {
  const askConfirmation = useConfirm();
  const { guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [settingSortBy, setSettingSortBy] = useState(SETTING_LIST_DEFAULTS.sortBy);
  const [settingSortDir, setSettingSortDir] = useState(SETTING_LIST_DEFAULTS.sortDir);
  const menuItems = SYSTEM_TABS;

  useEffect(() => {
    getUsers();
  }, []);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };
  
  const getUsers = () => {
    setLoading(true); // Set loading to true when fetching data
    fetch(apiUrl + `/api/roles`, { headers: { 'Content-Type': "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          'Authorization': getBasicAuthHeader(), } })
      .then(resp => resp.json())
      .then(resp => {
        setTableData(resp);
      })
      .catch(error => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false); // Set loading to false after data fetching is complete
      });
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialValue);
  };

  const onChange = (e) => {
    const { value, id } = e.target;
    setFormData({ ...formData, [id]: value });
  };

  const handleUpdate = (oldData) => {
    if (!guardAction('action.system.role.edit')) return;
    setFormData(oldData);
    setOpen(true);
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    // Set filter model to show only admin roles
    params.api.setFilterModel({
      role: {
        type: 'equals',
        filter: 'admin'
      }
    });
  };

  const handleFormSubmit = async () => {
    if (!guardAction('action.system.role.edit')) return;
    console.log(formData)
    if (formData) {
      const confirmed = await askConfirmation({
        title: 'Update setting?',
        message: 'Are you sure you want to update this row?',
        confirmLabel: 'Update',
      });
      if (!confirmed) {
        return;
      }
      setLoading(true);
      fetch(apiUrl + `/api/roles/${formData.id}`, {
        method: "PUT", body: JSON.stringify(formData), headers: { 'Content-Type': "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          'Authorization': getBasicAuthHeader(), }
      })
        .then(resp => resp.json())
        .then(resp => {
          handleClose();
          getUsers();
        })
        .catch(error => {
          console.error("Error updating user:", error);
        })
        .finally(() => {
          setLoading(false); // Set loading to false after form submission is complete
        });
    }
  };

  const columnDefs = [
    { headerName: "ID", field: "id", checkboxSelection: true },
    { headerName: "Name", field: "name", valueGetter: () => "Split Payment fee" },
    { headerName: "Role", field: "role", hide: true },
    { headerName: "Variable", field: "variable", valueGetter: () => "split_payment" },
    { headerName: "Value", field: "percentage" },
    { headerName: "Status", field: "status" },
    {
      headerName: "Actions", field: "id", cellRenderer: (params) => (
        <div>
          <MdEditNote
            data-tip="Update"
            style={{ margin: "10px", fontSize: "1.5em", color: "green", cursor: "pointer" }}
            onClick={() => handleUpdate(params.data)}
          />
          <Tooltip place="top" type="dark" effect="solid" />
        </div>
      )
    }
  ];

  const defaultColDef = {
    sortable: true,
    flex: 1,
    filter: true
  };

  const adminRows = useMemo(
    () => (tableData || []).filter((r) => r.role === 'admin'),
    [tableData],
  );

  const sortedTableData = useMemo(
    () => sortSettingsLocally(adminRows, settingSortBy, settingSortDir),
    [adminRows, settingSortBy, settingSortDir],
  );

  const handleSettingSortChange = (columnKey) => {
    const next = getNextSettingSort(columnKey, settingSortBy, settingSortDir);
    setSettingSortBy(next.sortBy);
    setSettingSortDir(next.sortDir);
  };

  return (
    <div>
      <DashboardNavbar menuItems={menuItems} />
      <div style={{ width: 'min(1180px, 95%)', margin: '0 auto' }}>
        <DataTable
          rows={sortedTableData}
          loading={loading}
          rowKey={(r, i) => r.id ?? i}
          sortBy={settingSortBy}
          sortDir={settingSortDir}
          onSortChange={handleSettingSortChange}
          primary={{
            header: <LabelWithHelp help={HELP.splitPaymentFee}>Split Payment fee</LabelWithHelp>,
            sortKey: SETTING_SORT_FIELDS.id,
            label: () => 'Split Payment fee',
            sub: (r) => `ID ${r.id}`,
          }}
          columns={[
            { header: <LabelWithHelp help={HELP.variable}>Variable</LabelWithHelp>, render: () => 'split_payment', muted: true },
            { header: <LabelWithHelp help={HELP.value}>Value</LabelWithHelp>, sortKey: SETTING_SORT_FIELDS.percentage, render: (r) => r.percentage, mono: true },
            { header: <LabelWithHelp help={HELP.status}>Status</LabelWithHelp>, sortKey: SETTING_SORT_FIELDS.status, render: (r) => r.status, muted: true },
          ]}
          actionsColumnWidth={52}
          actions={(r) => (
            <GroupedRowActionSheet
              entityLabel="Setting"
              title="Split Payment fee"
              menuAriaLabel="Setting actions"
              groupLabel="For this setting"
              groupItems={[
                { key: 'edit', icon: <Pencil {...ICON_PROPS} />, label: 'Edit', ...lockProps('action.system.role.edit') },
              ]}
              onAction={() => handleUpdate(r)}
            />
          )}
          emptyText="No settings found."
        />
      </div>
      <FormDialog
        open={open}
        handleClose={handleClose}
        data={formData}
        onChange={onChange}
        handleFormSubmit={handleFormSubmit}
        loading={loading}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </div>
  );
};

export default Grids;
